import { useEffect } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Sparkles, X, FileText } from 'lucide-react';
import { MarkdownText } from '@/components/MarkdownText';
import { explainCellApi, ExplainCellContext } from '@/lib/explainCell';

/**
 * "Explain this number" — a grounded AI narrative for one reported cell, citing
 * only the fp_facts behind it. Fails gracefully: the figures it explains are
 * unaffected by an AI error.
 */
export function ExplainCellPopover({
  wsId, context, onClose, onViewCitations,
}: {
  wsId: string;
  context: ExplainCellContext & { label: string };
  onClose: () => void;
  onViewCitations: (title: string, ids: string[]) => void;
}) {
  const { t } = useTranslation();
  const run = useMutation({ mutationFn: () => explainCellApi.explain(wsId, context) });

  // Generate once on open.
  useEffect(() => { run.mutate(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const planGate = run.error && /Growth or Enterprise plan/i.test((run.error as { response?: { data?: { message?: string } } })?.response?.data?.message ?? '');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal>
      <div className="absolute inset-0 bg-foreground/20 backdrop-blur-sm" onClick={onClose} aria-hidden />
      <div className="relative w-full max-w-md bg-background border border-border rounded-xl shadow-editorial p-5 space-y-3">
        <header className="flex items-center justify-between">
          <div className="flex items-center gap-2 min-w-0">
            <Sparkles className="h-4 w-4 text-violet-500 shrink-0" />
            <p className="text-sm font-semibold text-ink-900 truncate">{t('ai.explain_number', { defaultValue: 'Explain this number' })}</p>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X className="h-4 w-4" /></button>
        </header>
        <p className="text-[11px] text-muted-foreground num truncate">{context.label}</p>

        {run.isPending && <p className="text-sm text-muted-foreground animate-pulse">{t('ai.generating_narrative', { defaultValue: 'Generating narrative…' })}</p>}

        {run.error && (
          <p className="text-[13px] text-warn-700">
            {planGate
              ? ((run.error as { response?: { data?: { message?: string } } })?.response?.data?.message)
              : t('ai.explain_failed', { defaultValue: 'Couldn’t generate an explanation — the figures above are unaffected.' })}
          </p>
        )}

        {run.data && (
          <div className="space-y-3">
            <div className="text-[13.5px] text-ink-800 leading-relaxed"><MarkdownText>{run.data.narrative}</MarkdownText></div>
            <div className="flex items-center justify-between pt-1 border-t border-border/60">
              {run.data.citedFactIds.length > 0 ? (
                <button onClick={() => onViewCitations(context.label, run.data!.citedFactIds)} className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-emerald-700 hover:text-emerald-800">
                  <FileText className="h-3.5 w-3.5" /> {t('ai.view_cited_facts', { defaultValue: `View ${run.data.citedFactIds.length} cited facts`, count: run.data.citedFactIds.length })}
                </button>
              ) : <span className="text-[11px] text-muted-foreground">{t('ai.no_cited_facts', { defaultValue: '(no facts cited)' })}</span>}
              <span className="num text-[11px] text-muted-foreground">AI · ${run.data.costUsd.toFixed(2)}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
